import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Admin panel for the V2 wage formula parameters ("VG-Priority 2.0").
 * Editable: tetto_max, soglia_vg, vg_riferimento, esponente,
 *           eta_soglia, sconto_anno, pavimento_eta, riduzione_generale.
 */
export default function AdminWageParams() {
    const [params, setParams] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [err, setErr] = useState(null);
    const [divForm, setDivForm] = useState(null);
    const [divInit, setDivInit] = useState(null);
    const [divSaving, setDivSaving] = useState(false);
    const [divSavedAt, setDivSavedAt] = useState(null);
    const [divErr, setDivErr] = useState(null);

    useEffect(() => {
        api.get("/admin/settings/wages").then((r) => {
            setParams(r.data);
            setForm(r.data);
        }).catch((e) => setErr(e.message));
        api.get("/settings/divisions").then((r) => {
            const pct = {};
            ["TOP", "DOP", "OPS", "HOPE"].forEach((k) => { pct[k] = Math.round((r.data[k] ?? 0) * 100); });
            setDivForm(pct);
            setDivInit(pct);
        }).catch(() => {});
    }, []);

    const saveDiv = async () => {
        setDivErr(null);
        setDivSaving(true);
        try {
            const discounts = {};
            ["TOP", "DOP", "OPS", "HOPE"].forEach((k) => { discounts[k] = (Number(divForm[k]) || 0) / 100; });
            const r = await api.put("/admin/settings/divisions", { discounts });
            const pct = {};
            ["TOP", "DOP", "OPS", "HOPE"].forEach((k) => { pct[k] = Math.round((r.data[k] ?? 0) * 100); });
            setDivForm(pct);
            setDivInit(pct);
            setDivSavedAt(new Date());
        } catch (e) {
            setDivErr(e.response?.data?.detail || e.message);
        } finally {
            setDivSaving(false);
        }
    };

    const save = async () => {
        setErr(null);
        setSaving(true);
        try {
            const payload = {
                tetto_max: Number(form.tetto_max),
                soglia_vg: Number(form.soglia_vg),
                vg_riferimento: Number(form.vg_riferimento),
                esponente: Number(form.esponente),
                eta_soglia: Number(form.eta_soglia),
                sconto_anno: Number(form.sconto_anno),
                pavimento_eta: Number(form.pavimento_eta),
                riduzione_generale: Number(form.riduzione_generale),
            };
            const r = await api.put("/admin/settings/wages", payload);
            setParams(r.data);
            setSavedAt(new Date());
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setForm({
            tetto_max: 40_000_000,
            soglia_vg: 62,
            vg_riferimento: 89,
            esponente: 2.5,
            eta_soglia: 35,
            sconto_anno: 0.03,
            pavimento_eta: 0.75,
            riduzione_generale: 0.13,
        });
    };

    if (!params) return <div className="op-mono text-sm text-t8f95a3 animate-pulse">// CARICAMENTO…</div>;

    const dirty = JSON.stringify(form) !== JSON.stringify(params);

    return (
        <div data-testid="admin-wages-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// FORMULA STIPENDI</div>
            <h2 className="op-heading text-3xl mt-2 mb-3">Parametri "VG-Priority 2.0"</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-6 max-w-3xl leading-relaxed">
                Lo stipendio nuovo di ogni giocatore parte dallo <strong className="text-tffffff">stipendio attuale</strong>{" "}
                (importato dal vecchio sito) e applica:
                <code className="text-te8ff00 mx-1">tetto_vg</code>,
                <code className="text-te8ff00 mx-1">fattore_età</code> e
                <code className="text-te8ff00 mx-1">riduzione generale</code>.
                Modifica i parametri qui per ritarare l'economia senza toccare il codice.
            </p>

            {/* Formula visiva */}
            <div className="op-card p-4 mb-6 font-mono text-[12px] text-tc9ccd3 leading-relaxed bg-t0a0c0f" data-testid="formula-recap">
                <div className="op-label text-t2bb6ff mb-2">// FORMULA APPLICATA</div>
                <div>tetto_vg     = <span className="text-te8ff00">tetto_max</span> · ( max(0, VG − <span className="text-te8ff00">soglia_vg</span>) / (<span className="text-te8ff00">vg_riferimento</span> − <span className="text-te8ff00">soglia_vg</span>) ) <sup><span className="text-te8ff00">esponente</span></sup></div>
                <div>fattore_eta  = se età &gt; <span className="text-te8ff00">eta_soglia</span>: max(<span className="text-te8ff00">pavimento_eta</span>, 1 − <span className="text-te8ff00">sconto_anno</span>·(età − <span className="text-te8ff00">eta_soglia</span>)) altrimenti 1</div>
                <div>base         = stipendio_attuale · fattore_eta · (1 − <span className="text-te8ff00">riduzione_generale</span>)</div>
                <div className="text-tffffff font-bold mt-1">stipendio_nuovo = min(<span className="text-te8ff00">tetto_max</span>, base, tetto_vg)</div>
                <div className="text-t5c6370 mt-1">se VG &lt; soglia_vg → niente tetto_vg, solo min(tetto_max, base)</div>
            </div>

            {/* Form */}
            <div className="op-card p-5 mb-6">
                <div className="op-label mb-4">// PARAMETRI</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Field label="tetto_max (EUR)" sub="Stipendio massimo assoluto" testId="param-tetto-max">
                        <input
                            type="number"
                            value={form.tetto_max ?? ""}
                            onChange={(e) => setForm({ ...form, tetto_max: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="soglia_vg" sub="Sotto questo VG niente tetto VG" testId="param-soglia-vg">
                        <input
                            type="number"
                            value={form.soglia_vg ?? ""}
                            onChange={(e) => setForm({ ...form, soglia_vg: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="vg_riferimento" sub="VG a cui scatta tetto_max" testId="param-vg-rif">
                        <input
                            type="number"
                            value={form.vg_riferimento ?? ""}
                            onChange={(e) => setForm({ ...form, vg_riferimento: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="esponente" sub="Ripidità curva (2.5 default)" testId="param-esponente">
                        <input
                            type="number"
                            step="0.1"
                            value={form.esponente ?? ""}
                            onChange={(e) => setForm({ ...form, esponente: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="eta_soglia" sub="Sconto età parte SOPRA questa età" testId="param-eta-soglia">
                        <input
                            type="number"
                            value={form.eta_soglia ?? ""}
                            onChange={(e) => setForm({ ...form, eta_soglia: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="sconto_anno" sub="% sconto per anno oltre soglia (0.03 = 3%)" testId="param-sconto-anno">
                        <input
                            type="number"
                            step="0.01"
                            value={form.sconto_anno ?? ""}
                            onChange={(e) => setForm({ ...form, sconto_anno: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="pavimento_eta" sub="Sconto età non scende sotto (0.75 = 75%)" testId="param-pavimento">
                        <input
                            type="number"
                            step="0.01"
                            value={form.pavimento_eta ?? ""}
                            onChange={(e) => setForm({ ...form, pavimento_eta: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                    <Field label="riduzione_generale" sub="% sconto su tutti (0.13 = 13%)" testId="param-riduzione">
                        <input
                            type="number"
                            step="0.01"
                            value={form.riduzione_generale ?? ""}
                            onChange={(e) => setForm({ ...form, riduzione_generale: e.target.value })}
                            className="op-input w-full text-right"
                        />
                    </Field>
                </div>

                {err && (
                    <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mt-4" data-testid="wages-error">
                        {err}
                    </div>
                )}

                <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-t22252a">
                    <button onClick={reset} className="op-mono text-xs uppercase tracking-wider px-3 py-1.5 border border-t22252a hover:border-t8f95a3 text-t8f95a3 hover:text-tffffff transition-colors" data-testid="reset-defaults">
                        ↻ Reset valori di default
                    </button>
                    <div className="flex items-center gap-3">
                        {savedAt && !dirty && (
                            <span className="op-mono text-[10px] text-t2bb6ff" data-testid="wages-saved">
                                ✓ Salvato {savedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        )}
                        <button
                            onClick={save}
                            disabled={saving || !dirty}
                            className="op-btn op-btn-primary px-5 py-2 disabled:opacity-50"
                            data-testid="save-wages"
                        >
                            {saving ? "..." : "Salva e applica"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="op-mono text-[10px] text-t5c6370 leading-relaxed">
                ⚠ Le modifiche si applicano IMMEDIATAMENTE a tutti i calcoli stipendio del portale
                (pagine squadra, scheda finanziaria, anteprime rate, tabellone bilancio).
                Non c'è ricalcolo asincrono — al prossimo refresh ogni pagina mostra i nuovi valori.
            </div>

            {/* Sgravi fiscali per divisione */}
            {divForm && (
                <div className="op-card p-5 mt-8" data-testid="division-discounts">
                    <div className="op-label mb-1" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// SGRAVI FISCALI PER DIVISIONE</div>
                    <p className="op-mono text-xs text-t8f95a3 mb-4 max-w-3xl leading-relaxed">
                        Sconto applicato sul <strong className="text-tffffff">monte stipendi totale</strong> in base alla divisione
                        in cui milita la squadra (dopo il fattore VG11). Es. HOPE 40% = paga il 60% del monte ingaggi.
                        Si applica subito a bilancio e a tutte le rose.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {["TOP", "DOP", "OPS", "HOPE"].map((k) => (
                            <Field key={k} label={`${k} (%)`} sub="Sgravio sul monte stipendi" testId={`div-${k.toLowerCase()}`}>
                                <input
                                    type="number"
                                    min="0" max="100" step="1"
                                    value={divForm[k] ?? 0}
                                    onChange={(e) => setDivForm({ ...divForm, [k]: e.target.value })}
                                    className="op-input w-full text-right"
                                    data-testid={`div-input-${k.toLowerCase()}`}
                                />
                            </Field>
                        ))}
                    </div>
                    {divErr && (
                        <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mt-4" data-testid="div-error">{divErr}</div>
                    )}
                    <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-t22252a">
                        {divSavedAt && JSON.stringify(divForm) === JSON.stringify(divInit) && (
                            <span className="op-mono text-[10px] text-t2bb6ff" data-testid="div-saved">
                                ✓ Salvato {divSavedAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                        )}
                        <button
                            onClick={saveDiv}
                            disabled={divSaving || JSON.stringify(divForm) === JSON.stringify(divInit)}
                            className="op-btn op-btn-primary px-5 py-2 disabled:opacity-50"
                            data-testid="save-divisions"
                        >
                            {divSaving ? "..." : "Salva sgravi"}
                        </button>
                    </div>
                    <div className="op-mono text-[9px] text-t5c6370 mt-3">
                        Nota: il <strong>testo del Regolamento</strong> è separato — se cambi una % qui ricordati di aggiornarlo a parte se vuoi che combaci.
                    </div>
                </div>
            )}
        </div>
    );
}


function Field({ label, sub, children, testId }) {
    return (
        <label className="block" data-testid={testId}>
            <div className="op-label text-[10px] text-tc9ccd3">{label}</div>
            <div className="op-mono text-[9px] text-t5c6370 mb-1 leading-tight">{sub}</div>
            {children}
        </label>
    );
}
